// src/components/OfflineBanner.js
// Shows a persistent banner when the user is offline, and replays queued requests on reconnect
import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Box, Typography, Chip } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import { getPendingCount, replayQueue } from '../utils/offlineQueue';

export default function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(getPendingCount());
  const [syncSnack, setSyncSnack] = useState(null);

  const handleOnline = useCallback(async () => {
    setIsOffline(false);
    const count = getPendingCount();
    if (count > 0) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const { replayed, failed } = await replayQueue(token);
      setPendingCount(0);
      if (replayed > 0) {
        setSyncSnack(`✅ Back online — ${replayed} queued action${replayed > 1 ? 's' : ''} synced${failed > 0 ? `, ${failed} failed` : ''}`);
      } else {
        setSyncSnack('✅ Back online');
      }
    } else {
      setSyncSnack('✅ Back online');
    }
  }, []);

  const handleOffline = useCallback(() => {
    setIsOffline(true);
    setPendingCount(getPendingCount());
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Refresh pending count every 5s when offline
    const interval = isOffline ? setInterval(() => setPendingCount(getPendingCount()), 5000) : null;
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (interval) clearInterval(interval);
    };
  }, [handleOnline, handleOffline, isOffline]);

  return (
    <>
      {/* Offline persistent bar */}
      {isOffline && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            py: 0.75,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            boxShadow: 2
          }}
        >
          <WifiOffIcon fontSize="small" />
          <Typography variant="caption" fontWeight={600}>
            You're offline — changes will sync when you reconnect
          </Typography>
          {pendingCount > 0 && (
            <Chip
              icon={<SyncIcon style={{ fontSize: 12 }} />}
              label={`${pendingCount} pending`}
              size="small"
              sx={{ bgcolor: 'rgba(0,0,0,0.15)', color: 'inherit', height: 20, fontSize: '0.65rem' }}
            />
          )}
        </Box>
      )}

      {/* Sync success snackbar */}
      <Snackbar
        open={!!syncSnack}
        autoHideDuration={4000}
        onClose={() => setSyncSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSyncSnack(null)} severity="success" sx={{ borderRadius: 2 }}>
          {syncSnack}
        </Alert>
      </Snackbar>
    </>
  );
}
